import { message } from "antd";
import { COMPANY_INFO } from "./constants";

// ============================================
// DOWNLOAD HELPER - Universal
// ============================================
const triggerDownload = (blob, filename, mimeType) => {
  return new Promise((resolve) => {
    // ✅ Unique blob URL har baar
    const blobUrl = URL.createObjectURL(
      new Blob([blob], { type: mimeType })
    );

    // ✅ Fresh anchor
    const anchor = document.createElement("a");
    anchor.style.cssText =
      "display:none;position:fixed;top:-200px;left:-200px;";
    anchor.href = blobUrl;
    anchor.download = filename;
    anchor.rel = "noopener";

    // ✅ DOM mein add
    document.body.appendChild(anchor);

    // ✅ Click with small delay
    setTimeout(() => {
      anchor.click();

      // ✅ Cleanup
      setTimeout(() => {
        if (document.body.contains(anchor)) {
          document.body.removeChild(anchor);
        }
        URL.revokeObjectURL(blobUrl);
        resolve(true);
      }, 1000);
    }, 50);
  });
};

// ============================================
// PDF BUILDER
// ============================================
const buildPDF = async (data, columns, options = {}) => {
  const {
    title = "Report",
    subtitle = "",
    filename = "report",
    orientation = "landscape",
  } = options;

  if (!data || data.length === 0) {
    message.warning("No data to export");
    return false;
  }

  // ✅ Dynamic import - fresh load
  const { default: jsPDF } = await import("jspdf");
  const { default: autoTable } = await import(
    "jspdf-autotable"
  );

  const brandColor = COMPANY_INFO?.brandColor || [255, 0, 85];
  const companyName = COMPANY_INFO?.name || "Company";
  const companyTagline = COMPANY_INFO?.tagline || "";
  const companyCopyright = COMPANY_INFO?.copyright || "";

  // ✅ New instance
  const doc = new jsPDF({
    orientation,
    unit: "mm",
    format: "a4",
  });

  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const [r, g, b] = brandColor;

  // ── Header Bar ──────────────────────────────
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, W, 22, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(companyName.toUpperCase(), 14, 14);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(companyTagline, W - 14, 14, { align: "right" });

  // ── Title ────────────────────────────────────
  let Y = 32;
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(title, 14, Y);

  if (subtitle) {
    Y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    doc.text(subtitle, 14, Y);
  }

  // ── Meta Info ────────────────────────────────
  Y += 8;
  const genDate = new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${genDate}`, 14, Y);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(r, g, b);
  doc.text(
    `Total Records: ${data.length}`,
    W - 14,
    Y,
    { align: "right" }
  );

  // ── Divider ──────────────────────────────────
  Y += 4;
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.5);
  doc.line(14, Y, W - 14, Y);

  // ── Table ────────────────────────────────────
  const headers = columns.map((c) => c.label);

  const rows = data.map((row, i) =>
    columns.map((col) => {
      if (col.key === "sno") return String(i + 1);
      if (col.format && typeof col.format === "function") {
        try {
          const v = col.format(row);
          return v == null ? "N/A" : String(v);
        } catch {
          return "N/A";
        }
      }
      const v = row[col.key];
      return v == null ? "N/A" : String(v);
    })
  );

  autoTable(doc, {
    startY: Y + 6,
    head: [headers],
    body: rows,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: [230, 230, 230],
      lineWidth: 0.1,
      valign: "middle",
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [r, g, b],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
      halign: "left",
      cellPadding: 4,
    },
    bodyStyles: { textColor: [50, 50, 50] },
    alternateRowStyles: { fillColor: [250, 250, 252] },
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
    },
    margin: { top: 10, left: 14, right: 14, bottom: 25 },
    didDrawPage: () => {
      const total = doc.internal.getNumberOfPages();
      const current =
        doc.internal.getCurrentPageInfo().pageNumber;

      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(14, H - 15, W - 14, H - 15);

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(150, 150, 150);
      doc.text(companyCopyright, 14, H - 8);
      doc.text(
        `Page ${current} of ${total}`,
        W / 2,
        H - 8,
        { align: "center" }
      );
      doc.setFont("helvetica", "bold");
      doc.setTextColor(r, g, b);
      doc.text("CONFIDENTIAL", W - 14, H - 8, {
        align: "right",
      });
    },
  });

  // ── Summary ──────────────────────────────────
  const finalY = doc.lastAutoTable?.finalY || Y + 20;

  if (finalY < H - 45) {
    doc.setFillColor(248, 249, 255);
    doc.roundedRect(14, finalY + 8, W - 28, 18, 2, 2, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    doc.text("Summary", 18, finalY + 16);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(
      `${data.length} records exported on ${genDate}`,
      18,
      finalY + 22
    );
  }

  // ✅ Output as ArrayBuffer
  return doc.output("arraybuffer");
};

// ============================================
// MAIN EXPORT FUNCTION
// ============================================
export const exportToPDF = async (
  data,
  columns,
  options = {}
) => {
  try {
    const pdfBuffer = await buildPDF(data, columns, options);
    if (!pdfBuffer) return false;

    const { filename = "report" } = options;
    const timestamp = Date.now();
    const date = new Date().toISOString().split("T")[0];
    const fullFilename = `${filename}_${date}_${timestamp}.pdf`;

    await triggerDownload(
      pdfBuffer,
      fullFilename,
      "application/pdf"
    );

    message.success(
      `✅ PDF exported successfully (${data.length} records)`
    );
    return true;
  } catch (error) {
    console.error("PDF Export Error:", error);
    message.error(`PDF export failed: ${error.message}`);
    return false;
  }
};

// ============================================
// PREVIEW FUNCTION
// ============================================
export const previewPDF = async (data, columns, options = {}) => {
  const {
    title = "Report",
    orientation = "landscape",
  } = options;

  if (!data || data.length === 0) {
    message.warning("No data to preview");
    return false;
  }

  try {
    const { default: jsPDF } = await import("jspdf");
    const { default: autoTable } = await import(
      "jspdf-autotable"
    );

    const brandColor =
      COMPANY_INFO?.brandColor || [255, 0, 85];
    const [r, g, b] = brandColor;
    const companyName = COMPANY_INFO?.name || "Company";

    const doc = new jsPDF({
      orientation,
      unit: "mm",
      format: "a4",
    });

    const W = doc.internal.pageSize.getWidth();

    doc.setFillColor(r, g, b);
    doc.rect(0, 0, W, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(companyName.toUpperCase(), 14, 14);

    doc.setTextColor(30, 30, 30);
    doc.setFontSize(16);
    doc.text(title, 14, 32);

    const headers = columns.map((c) => c.label);
    const rows = data.map((row, i) =>
      columns.map((col) => {
        if (col.key === "sno") return String(i + 1);
        if (col.format) {
          try {
            return String(col.format(row) || "N/A");
          } catch {
            return "N/A";
          }
        }
        return String(row[col.key] || "N/A");
      })
    );

    autoTable(doc, {
      startY: 40,
      head: [headers],
      body: rows,
      theme: "grid",
      headStyles: { fillColor: [r, g, b] },
    });

    const pdfBlob = doc.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return true;
  } catch (error) {
    console.error("PDF Preview Error:", error);
    message.error("Preview failed");
    return false;
  }
};